import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardList, Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StickyHeader from "@/components/StickyHeader";
import SurveiPeriodeDialog from "@/components/SurveiPeriodeDialog";
import SurveiCakupan from "@/components/SurveiCakupan";
import SurveiHasil from "@/components/SurveiHasil";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatTanggalPendek } from "@/lib/datetime";
import SurveiTautanPeriode from "@/components/SurveiTautanPeriode";
import SurveiButirVersi from "@/components/SurveiButirVersi";
import SurveiMasterKepnakes from "@/components/SurveiMasterKepnakes";
import SurveiCakupanKepnakes from "@/components/SurveiCakupanKepnakes";
import SurveiBadgeKelompok from "@/components/SurveiBadgeKelompok";
import {
    KELOMPOK_KEPNAKES, LABEL_KEADAAN, LABEL_TAB_SURVEI, periodeBawaan, TAB_SURVEI_BACA, TAB_SURVEI_KELOLA, TEKS_FITUR_NONAKTIF_BACA,
} from "@/lib/survei";
import SurveiGabungan from "@/components/SurveiGabungan";
import { useAuth } from "@/context/AuthContext";
import { bolehKelolaSurvei } from "@/lib/roles";

const WARNA_KEADAAN = { buka: "bg-emerald-600 text-white", belum: "bg-sky-600 text-white" };

// Admin Survei Kepuasan Peserta Didik. Super Admin: kelola semua tab. Admin UFP: baca-saja (tab Cakupan, Hasil, Tautan, Butir,
// Master Kepnakes; Gabungan di dalam Hasil; tanpa tombol tulis, server tetap menolak 403). Peran lain ditolak rute & API.
export default function SurveiAdmin() {
    const { user } = useAuth();
    const kelola = bolehKelolaSurvei(user?.role);
    const daftarTab = kelola ? TAB_SURVEI_KELOLA : TAB_SURVEI_BACA;
    const [periode, setPeriode] = useState([]);
    const [fiturAktif, setFiturAktif] = useState(true);
    const [dipilih, setDipilih] = useState("");
    const [tab, setTab] = useState(daftarTab[0]);
    const [dialog, setDialog] = useState({ open: false, periode: null });
    const [konfirmasi, setKonfirmasi] = useState(null); // {jenis: "buka"|"tutup"|"hapus", periode}
    const [versi, setVersi] = useState([]); // daftar versi butir (tab Butir & form periode baru)

    const muat = useCallback(() => api.get("/survei/periode")
        .then((r) => {
            setPeriode(r.data.periode);
            setFiturAktif(r.data.fitur_aktif);
            setDipilih((d) => (r.data.periode.some((p) => p.id === d) ? d : periodeBawaan(r.data.periode)));
        })
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), []);
    const muatVersi = useCallback(() => api.get("/survei/versi-butir")
        .then((r) => setVersi(r.data?.versi || []))
        .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail))), []);
    useEffect(() => { muat(); muatVersi(); }, [muat, muatVersi]);
    const periodeDipilih = periode.find((p) => p.id === dipilih);
    const dipilihKepnakes = periodeDipilih?.kelompok === KELOMPOK_KEPNAKES;

    const simpanPeriode = async (form) => {
        try {
            if (dialog.periode) await api.put(`/survei/periode/${dialog.periode.id}`, form);
            else await api.post("/survei/periode", form);
            toast.success(dialog.periode ? "Periode diperbarui" : "Periode dibuat");
            setDialog({ open: false, periode: null });
            muat(); muatVersi();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const jalankanKonfirmasi = async () => {
        const { jenis, periode: p } = konfirmasi;
        try {
            if (jenis === "hapus") await api.delete(`/survei/periode/${p.id}`);
            else await api.put(`/survei/periode/${p.id}`, { aktif: jenis === "buka" });
            toast.success({ buka: "Periode dibuka", tutup: "Periode ditutup", hapus: "Periode dihapus" }[jenis]);
            muat();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setKonfirmasi(null);
    };

    const pemilihPeriode = (
        <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-600">Periode</span>
            <Select value={dipilih} onValueChange={setDipilih}>
                <SelectTrigger className="w-72" data-testid="survei-pilih-periode"><SelectValue placeholder="Pilih periode" /></SelectTrigger>
                <SelectContent>{periode.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}{p.kelompok === KELOMPOK_KEPNAKES ? " · Kepnakes" : ""}</SelectItem>)}</SelectContent>
            </Select>
            {periodeDipilih && <SurveiBadgeKelompok kelompok={periodeDipilih.kelompok} testId="survei-pilih-periode-kelompok" />}
        </div>
    );

    return (
        <div className="mx-auto max-w-6xl space-y-4 px-6 py-8 lg:px-8">
            <StickyHeader>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Survei</div>
                <h1 className="flex items-center gap-2 text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    <ClipboardList className="h-7 w-7 text-teal-600" /> Survei Kepuasan Peserta Didik
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Setiap periode memiliki tautan sendiri. Jawaban disimpan tanpa identitas; halaman ini hanya menampilkan angka.
                </p>
            </StickyHeader>

            {!fiturAktif && (
                <div role="alert" data-testid="survei-fitur-nonaktif" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                    <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                    {kelola ? "Survei belum aktif di server (pengaturan SURVEI_PEPPER belum diisi). Tautan publik menolak semua pengisian sampai pengaturan itu diisi." : TEKS_FITUR_NONAKTIF_BACA}
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                {/* PR #112: 6 tab -> boleh turun baris di layar sempit (tanpa gulir mendatar halaman) */}
                <TabsList className="h-auto max-w-full flex-wrap justify-start">
                    {daftarTab.map((t) => <TabsTrigger key={t} value={t} data-testid={`survei-tab-${t}`}>{LABEL_TAB_SURVEI[t]}</TabsTrigger>)}
                </TabsList>

                {kelola && <TabsContent value="periode">
                    <Card className="overflow-hidden">
                        <div className="flex items-center justify-between gap-3 border-b p-4">
                            <p className="text-xs text-slate-500">Hanya satu periode per kelompok (Kedokteran / Kepnakes) yang boleh dibuka pada satu waktu.</p>
                            <Button onClick={() => setDialog({ open: true, periode: null })} className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="survei-periode-baru">
                                <Plus className="mr-2 h-4 w-4" /> Periode Baru
                            </Button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="compact w-full" data-testid="survei-tabel-periode">
                                <thead className="bg-slate-100">
                                    <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-600">
                                        <th>Nama</th><th>Kelompok</th><th>Tanggal</th><th>Versi butir</th><th>Keadaan</th><th className="text-right">Jawaban</th><th className="pr-4 text-right">Aksi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {periode.length === 0 ? (
                                        <tr><td colSpan={7} className="py-8 text-center text-slate-400">Belum ada periode.</td></tr>
                                    ) : periode.map((p) => (
                                        <tr key={p.id} className="border-t border-slate-200" data-testid={`survei-periode-${p.id}`}>
                                            <td className="font-semibold text-slate-800">{p.nama}</td>
                                            <td><SurveiBadgeKelompok kelompok={p.kelompok} testId={`survei-periode-kelompok-${p.id}`} /></td>
                                            <td className="text-xs">{formatTanggalPendek(p.tanggal_mulai)} – {formatTanggalPendek(p.tanggal_selesai)}</td>
                                            <td className="text-xs" data-testid={`survei-periode-versi-${p.id}`}>{p.versi_butir?.nama || "–"}</td>
                                            <td><Badge variant="outline" className={WARNA_KEADAAN[p.keadaan] || ""}>{LABEL_KEADAAN[p.keadaan]}</Badge></td>
                                            <td className="text-right tabular-nums">{p.jumlah_jawaban}</td>
                                            <td className="whitespace-nowrap pr-2 text-right">
                                                <Button size="sm" variant="outline" onClick={() => setKonfirmasi({ jenis: p.aktif ? "tutup" : "buka", periode: p })}
                                                        data-testid={`survei-${p.aktif ? "tutup" : "buka"}-${p.id}`}>
                                                    {p.aktif ? "Tutup" : "Buka"}
                                                </Button>
                                                <Button size="sm" variant="ghost" aria-label={`Ubah ${p.nama}`} onClick={() => setDialog({ open: true, periode: p })}><Pencil className="h-4 w-4" /></Button>
                                                {p.jumlah_jawaban === 0 && (
                                                    <Button size="sm" variant="ghost" aria-label={`Hapus ${p.nama}`} className="text-rose-600" onClick={() => setKonfirmasi({ jenis: "hapus", periode: p })}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </TabsContent>}

                <TabsContent value="tautan">
                    <SurveiTautanPeriode periode={periode} />
                </TabsContent>

                <TabsContent value="cakupan">
                    {pemilihPeriode}
                    {dipilihKepnakes ? <SurveiCakupanKepnakes periodeId={dipilih} onBerubah={muat} bacaSaja={!kelola} /> : <SurveiCakupan periodeId={dipilih} />}
                </TabsContent>

                <TabsContent value="hasil">
                    {pemilihPeriode}
                    <SurveiHasil periodeId={dipilih} periodeAktif={!!periodeDipilih?.aktif}
                                 jumlahButir={periodeDipilih?.versi_butir?.jumlah_butir} kelompok={dipilihKepnakes ? KELOMPOK_KEPNAKES : "kedokteran"} bacaSaja={!kelola} />
                    {!kelola && (
                        <section className="mt-8 space-y-3 border-t border-slate-200 pt-6" data-testid="survei-hasil-gabungan">
                            <h2 className="text-lg font-bold text-slate-900">Gabungan</h2>
                            <SurveiGabungan periode={periode} />
                        </section>
                    )}
                </TabsContent>

                {kelola && (
                    <TabsContent value="gabungan">
                        <SurveiGabungan periode={periode} />
                    </TabsContent>
                )}

                <TabsContent value="butir">
                    <SurveiButirVersi versi={versi} onBerubah={muatVersi} bacaSaja={!kelola} />
                </TabsContent>

                <TabsContent value="master">
                    <SurveiMasterKepnakes />
                </TabsContent>
            </Tabs>

            {kelola && <SurveiPeriodeDialog open={dialog.open} periode={dialog.periode} versi={versi} onClose={() => setDialog({ open: false, periode: null })} onSimpan={simpanPeriode} />}

            <AlertDialog open={!!konfirmasi} onOpenChange={(o) => !o && setKonfirmasi(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {konfirmasi && { buka: "Buka periode?", tutup: "Tutup periode?", hapus: "Hapus periode?" }[konfirmasi.jenis]}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {konfirmasi?.jenis === "buka" && `Tautan survei akan menerima pengisian untuk "${konfirmasi.periode.nama}" selama tanggal periode berlaku.`}
                            {konfirmasi?.jenis === "tutup" && `Tautan survei berhenti menerima pengisian. Jawaban yang sudah masuk tetap tersimpan.`}
                            {konfirmasi?.jenis === "hapus" && `Periode "${konfirmasi.periode.nama}" belum punya jawaban dan akan dihapus permanen.`}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={jalankanKonfirmasi} data-testid="survei-konfirmasi-ya">Ya, lanjutkan</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
